import React, { FunctionComponent, useContext, useEffect } from 'react'
import { useIntl } from 'react-intl'
import { View } from 'react-native'
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated'

import { LinearGradient } from 'expo-linear-gradient'
import { useTheme } from 'styled-components/native'

import { ProgressBarProps } from '@lyrahealth-inc/shared-app-logic/src/features/header/types'

import { AccessibilityRolesNative } from '../../constants'
import { AppContext } from '../../context'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

export const ProgressBar: FunctionComponent<ProgressBarProps> = ({
  progress: percentageFromProps,
  height = 5,
  color: colorFromProps,
  borderRadius = 200,
  backgroundColor,
  shouldAnimate = true,
  width = -1,
  duration = 300,
  style,
  accessibilityLiveRegion,
  showStopIndicator = false,
  customStopIndicatorStyle,
}) => {
  const { progressBarPercentage: percentageFromContext } = useContext(AppContext)
  const givenProgress = percentageFromProps ?? percentageFromContext ?? 0
  const progress = Number.isNaN(givenProgress) ? 0 : givenProgress
  const { colors } = useTheme() as ThemeType
  const color = colorFromProps ?? colors.progressIndicator
  const { progressBar } = colors.components
  const { formatMessage } = useIntl()
  const sanitizedProgress = Math.max(Math.min(progress, 1), 0)
  const sharedProgress = useSharedValue(sanitizedProgress)
  const barBackgroundColor =
    backgroundColor ??
    (percentageFromContext !== undefined
      ? [colors.progressIndicatorBackgroundTop, colors.progressIndicatorBackgroundBottom]
      : progressBar.background)

  useEffect(() => {
    sharedProgress.value = withTiming(sanitizedProgress, { duration: shouldAnimate ? duration : 0 })
  }, [duration, sanitizedProgress, sharedProgress, shouldAnimate])

  const animatedStyles = useAnimatedStyle(() => ({
    width: `${sharedProgress.value * 100}%`,
  }))

  const progressBarPercentage = Math.round(sanitizedProgress * 100)

  return (
    <LinearGradient
      colors={
        Array.isArray(barBackgroundColor)
          ? (barBackgroundColor as ArrayMinTwo<string>)
          : ([barBackgroundColor, barBackgroundColor] as ArrayMinTwo<string>)
      }
      accessibilityLiveRegion={accessibilityLiveRegion}
      accessibilityLabel={formatMessage(
        {
          defaultMessage: 'Progress bar percentage at {progressBarPercentage}',
          description: 'This bar shows the overall progress of an assessment or lesson to completion',
        },
        {
          progressBarPercentage,
        },
      )}
      accessible={true}
      testID={tID(`ProgressBar-percentage-${sanitizedProgress * 100}`)}
      accessibilityRole={AccessibilityRolesNative.PROGRESS_BAR}
      style={[
        {
          width,
          borderRadius,
          justifyContent: 'center',
        },
        style,
      ]}
    >
      <Animated.View style={[{ height, backgroundColor: color || progressBar.fill, borderRadius }, animatedStyles]} />
      {showStopIndicator && (
        <View
          testID={tID('ProgressBar-stopIndicator')}
          style={{
            position: 'absolute',
            right: 0,
            width: height,
            height,
            borderRadius: height / 2,
            backgroundColor: color,
            ...customStopIndicatorStyle,
          }}
        />
      )}
    </LinearGradient>
  )
}
